import { UOM } from '../../../../constants/commonConstants';
import {
  ALTERNATE,
  CLASSIC,
  DOWNSELL,
  GENUINE,
  REMAN,
  UPSELL
} from '../../../../constants/productAssociation';

// Types of merchandising associations applicable to Compare Alternate Parts
const types = [CLASSIC, REMAN, GENUINE, UPSELL, DOWNSELL, ALTERNATE];

/**
 * Indicates whether or not a product has been replaced
 *
 * @param {Obect} productPurchaseDetails Price & Availability details for a product
 * @returns True if the product is replaced. False otherwise.
 */
export const isReplaced = (productPurchaseDetails = {}) => {
  const isIndirectlyReplaced = productPurchaseDetails.indirectReplacement;
  const replacementParts = productPurchaseDetails.replacementParts;
  const isDirectlyReplaced =
    Array.isArray(replacementParts) && replacementParts.length > 0;
  return isDirectlyReplaced || isIndirectlyReplaced;
};

/**
 * A filter for merchandising associations that removes:
 * - association types not included in the 'types' array above
 * - replaced parts
 * - non-cat parts
 */
export const filterAssociations = (assoc, priceAndAvailability = {}) => {
  const isCorrectAssociation = types.includes(assoc.associationType);
  const productPA = priceAndAvailability?.[assoc.uniqueId] ?? {};
  const isReplacedPart = isReplaced(productPA);
  const isCatPart = productPA?.isCatPart;
  return isCorrectAssociation && !isReplacedPart && isCatPart !== false;
};

/**
 * Sorts merchandising associations by the order of the types array above
 */
export const sortByAssociationType = (a, b) =>
  types.indexOf(a.associationType) - types.indexOf(b.associationType);

/**
 * Builds an array of all the unique spec names
 */
export const getSpecs = (productsToCompare = [], unitOfMeasure = UOM.us) => {
  const attributes = productsToCompare
    .map(p =>
      p.attributes
        ?.filter(a => !a.uom || a.uom === unitOfMeasure) // accept blank or matching UOM
        ?.map(a => a.name)
    )
    .filter(a => !!a)
    .flat();
  return [...new Set(attributes ?? [])];
};

/**
 * Determines if brand should display or not. Will only display if multiple manufacturers exist
 *
 * @param {Array} productsToCompare A list of products
 * @returns true if brand should be shown. False otherwise.
 */
export const shouldShowBrand = productsToCompare =>
  new Set(
    productsToCompare.map(p => p.manufacturer?.toLowerCase()).filter(m => !!m)
  ).size > 1;
